import { Dialog, DialogId, DialogType } from '../../../../src/feature/narrator/narrator.model';
import { canHoldMore } from '../../inventory/helpers';
import { weightedPick } from '../../random';

// One tap -> a weighted outcome. ~70% a handful of worms (three flavour variants),
// ~30% a find *instead*. A find is a line to read, not loot: only the coin is
// kept, the rest are pure flavour, so nothing clutters the pack. Same pattern as
// the fishing session.
function resolveDig(): DialogId {
  return weightedPick([
    ['worms_a', 24],
    ['worms_b', 23],
    ['worms_c', 23],
    ['find_coin', 10],
    ['find_boot', 4],
    ['find_key', 4],
    ['find_tooth', 4],
    ['find_squirrel', 4],
    ['find_whistle', 4],
  ] as const);
}

const DIG = { text: 'Dig in the earth for worms.', nextDialogId: resolveDig, condition: () => canHoldMore('worm') };
// After a find you still have no bait, so "Dig again" is offered. After worms you
// hold your one handful, so those nodes only offer "Step away" (no pointless dig,
// and it sidesteps the option-render race on canHoldMore).
const AGAIN = { text: 'Dig again.', nextDialogId: resolveDig, condition: () => canHoldMore('worm') };
const AWAY: { text: string; nextDialogId: DialogId } = { text: 'Step away.', nextDialogId: 'start' };

export const dialog: Dialog[] = [
  {
    id: 'start',
    type: DialogType.description,
    text: 'You stand at the foot of the old oak. The trunk is wide and rough, and the earth at its roots is dark and soft. Birds call from the branches above.',
    options: [DIG, { text: 'Look up into the tree.', nextDialogId: 'look_tree' }],
  },

  // ~70% -- a handful of worms: one grant, three different reads. You have your
  // bait now, so these only step away.
  {
    id: 'worms_a',
    type: DialogType.description,
    text: "You push aside the wet earth at the oak's roots. The soil is dark and full of life. You fold **a handful of worms** into a leaf.",
    effects: [{ type: 'grant', item: 'worm', quantity: 1 }],
    options: [AWAY],
  },
  {
    id: 'worms_b',
    type: DialogType.description,
    text: 'You turn the cool soil with your fingers. The worms twist away from the light, and you pick out the fattest. **A handful of worms**, wrapped and ready.',
    effects: [{ type: 'grant', item: 'worm', quantity: 1 }],
    options: [AWAY],
  },
  {
    id: 'worms_c',
    type: DialogType.description,
    text: 'The ground lies soft in the shade of the oak. You dig, and the worms come up easily. Soon you have **a handful of worms** tucked away.',
    effects: [{ type: 'grant', item: 'worm', quantity: 1 }],
    options: [AWAY],
  },

  // ~30% -- a find *instead of* worms. Only the coin is kept; the rest are flavour
  // (no item enters the pack). You have no bait, so dig again.
  {
    id: 'find_coin',
    type: DialogType.description,
    text: 'Your fingers close on something small and hard. An old **denier**, green with age. Someone lost it here long before you.',
    effects: [{ type: 'grant', item: 'coins', quantity: 1 }],
    options: [AGAIN, AWAY],
  },
  {
    id: 'find_boot',
    type: DialogType.description,
    text: 'You pull up something stiff and heavy. A single old boot, packed with soil. The river gives up boots too. Where do they all come from? You leave it in the dirt.',
    options: [AGAIN, AWAY],
  },
  {
    id: 'find_key',
    type: DialogType.description,
    text: 'Cold iron turns up under your fingers. A heavy key, red with rust. It opens nothing you will ever find, so you drop it back.',
    options: [AGAIN, AWAY],
  },
  {
    id: 'find_tooth',
    type: DialogType.description,
    text: "A yellow tooth works loose from the dirt, too big for any man. A pig's, most likely. They root everywhere in Beggen.",
    options: [AGAIN, AWAY],
  },
  {
    id: 'find_squirrel',
    type: DialogType.description,
    text: "You break into a neat store of acorns, a squirrel's whole winter. You have made an enemy for life, and cover it back over.",
    options: [AGAIN, AWAY],
  },
  {
    id: 'find_whistle',
    type: DialogType.description,
    text: "A child's whistle, cut from a marsh reed and long gone soft. Some Dommeldange child must have dropped it, chasing something this far up the road.",
    options: [AGAIN, AWAY],
  },

  // Flavour.
  {
    id: 'look_tree',
    type: DialogType.description,
    text: 'The branches of the oak spread out wide. Small birds move from one to another. The tree must have stood here for a long time.',
    options: [{ text: 'Sit down at the foot of the tree.', nextDialogId: 'rest' }, DIG],
  },
  {
    id: 'rest',
    type: DialogType.description,
    text: 'You sit with your back against the trunk. The bark is rough through your shirt. For a while, you do nothing at all.',
    options: [{ text: 'Stand up and step away.', nextDialogId: 'start' }],
  },
];
